import React, { useRef, useLayoutEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../app/store";
import { addLayer, setActiveLayerId } from "../../../reducers/editorSlice";
import TitleIcon from '@mui/icons-material/Title';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Slider from '@mui/material/Slider';
import { useUploadMediaMutation } from "../../../services/auth";
import { toast } from "react-toastify";
import LayersList from "../layers-list";
import Loader from "../../../constants/Loader";
import RemoveBackgroundModal from "../modals/RemoveBackgroundModal";
import {
  removeImageBackground,
  BackgroundRemovalProgress,
} from "../../../services/backgroundRemoval";

interface Props {
  setActiveOverlay?: (overlay: string) => void;
  autoOpenPicker?: boolean;
  onPickerOpened?: () => void;
}

const UploadSettingsSidebar: React.FC<Props> = ({
  setActiveOverlay,
  autoOpenPicker = false,
  onPickerOpened,
}) => {
  const dispatch = useDispatch();
  const layers = useSelector((state: RootState) => state.editor.layers);
  const activeLayerId = useSelector((state: RootState) => state.editor.activeLayerId);
  const selectedObjectProps = useSelector((state: RootState) => state.editor.selectedObjectProps);
  const isSavingDesign = useSelector((state: RootState) => state.editor.isSavingDesign);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasAutoOpenedRef = useRef(false);
  const isOperationInProgressRef = useRef(false);

  // Background removal modal and processing state
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [bgProgress, setBgProgress] = useState<BackgroundRemovalProgress | null>(null);
  const [bgError, setBgError] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (autoOpenPicker && !hasAutoOpenedRef.current && fileInputRef.current) {
      hasAutoOpenedRef.current = true;
      fileInputRef.current.click();
      onPickerOpened?.();
    }
    if (!autoOpenPicker) {
      hasAutoOpenedRef.current = false;
    }
  }, [autoOpenPicker, onPickerOpened]);

  React.useEffect(() => {
    const handleDirectTrigger = () => {
      fileInputRef.current?.click();
    };
    window.addEventListener("trigger-upload-image", handleDirectTrigger);
    return () => window.removeEventListener("trigger-upload-image", handleDirectTrigger);
  }, []);

  const [uploadMediaApi, { isLoading: isUploadingMedia }] = useUploadMediaMutation();

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image size must be less than 10MB");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      setPendingFile(file);
      setBgError(null);
      setBgProgress(null);
      setIsModalOpen(true);
    }
  };

  const uploadAndAddImage = async (fileToUpload: File, successMessage: string) => {
    const formData = new FormData();
    formData.append("media_file", fileToUpload);
    formData.append("file_type", "img");

    try {
      const response = await uploadMediaApi(formData).unwrap();
      const remoteUrl = response?.data?.media_file_url || response?.media_file_url || "";
      const displayUrl = URL.createObjectURL(fileToUpload);
      const layerName = response?.data?.media_file_name || response?.media_file_name || fileToUpload.name || "Uploaded Image";
      const layerId = `image-${Date.now()}`;
      const mediaId = response?.data?.id ?? response?.id;

      dispatch(addLayer({ layerId, layerType: 'image', layerName }));
      dispatch(setActiveLayerId(layerId));

      const event = new CustomEvent('add-fabric-image', {
        detail: {
          url: displayUrl,
          layerId,
          layerName,
          mediaId,
          mediaFileUrl: remoteUrl || displayUrl,
        }
      });
      window.dispatchEvent(event);
      toast.success(successMessage);

      // Clean up modal state and reset file input
      setIsModalOpen(false);
      setPendingFile(null);
      setBgError(null);
      setBgProgress(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: unknown) {
      const err = error as { data?: { message?: string }; message?: string };
      const msg = err?.data?.message || err?.message || "Failed to upload image";
      toast.error(msg);
      setBgError(msg);
    }
  };

  const handleKeepBackground = async () => {
    if (isOperationInProgressRef.current || !pendingFile) return;
    isOperationInProgressRef.current = true;
    try {
      await uploadAndAddImage(pendingFile, "Image uploaded successfully!");
    } finally {
      isOperationInProgressRef.current = false;
    }
  };

  const handleRemoveBackground = async () => {
    if (isOperationInProgressRef.current || !pendingFile) return;
    isOperationInProgressRef.current = true;
    setIsRemovingBg(true);
    setBgError(null);
    setBgProgress({
      status: 'loading',
      message: 'Loading background removal model...',
    });

    try {
      const processedFile = await removeImageBackground(pendingFile, (progress) => {
        setBgProgress(progress);
      });

      setBgProgress({
        status: 'processing',
        message: 'Uploading transparent image...',
      });

      await uploadAndAddImage(processedFile, "Image uploaded with background removed!");
    } catch (error: unknown) {
      const err = error as { message?: string };
      setBgError(err?.message || "Failed to remove image background. You can retry or keep the original background.");
    } finally {
      setIsRemovingBg(false);
      isOperationInProgressRef.current = false;
    }
  };

  const handleCancelModal = () => {
    if (isRemovingBg || isUploadingMedia) return;
    setIsModalOpen(false);
    setPendingFile(null);
    setBgError(null);
    setBgProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

    const handleDeleteLayer = (layerId: string) => {
      window.dispatchEvent(new CustomEvent('remove-fabric-object', { detail: { layerId } }));
    };

    const handleSliderChange = (prop: string, value: number) => {
      if (!activeLayerId) return;
      const event = new CustomEvent('update-fabric-props', {
        detail: { layerId: activeLayerId, props: { [prop]: value } }
      });
      window.dispatchEvent(event);
    };

    const handlePositionSelect = (alignX: 'left' | 'center' | 'right', alignY: 'top' | 'center' | 'bottom') => {
      if (!activeLayerId) return;
      window.dispatchEvent(new CustomEvent('position-fabric-object', {
        detail: { layerId: activeLayerId, alignX, alignY }
      }));
    };

    const handleCoordChange = (axis: 'left' | 'top', value: string) => {
      if (!activeLayerId) return;
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return;
      window.dispatchEvent(new CustomEvent('update-fabric-props', {
        detail: { layerId: activeLayerId, props: { [axis]: numeric } }
      }));
    };

    const getActiveAlign = () => {
      if (!selectedObjectProps || selectedObjectProps.left === undefined || selectedObjectProps.top === undefined) {
        return 'center-center';
      }
      const lw = 2048;
      const lh = 2048;
      const sw = selectedObjectProps.scaledWidth || 0;
      const sh = selectedObjectProps.scaledHeight || 0;
      const ox = selectedObjectProps.originX || 'left';
      const oy = selectedObjectProps.originY || 'top';
      const cx = ox === 'center' ? selectedObjectProps.left : ox === 'right' ? selectedObjectProps.left - sw / 2 : selectedObjectProps.left + sw / 2;
      const cy = oy === 'center' ? selectedObjectProps.top : oy === 'bottom' ? selectedObjectProps.top - sh / 2 : selectedObjectProps.top + sh / 2;
      const col = cx < lw / 3 ? 'left' : cx > (2 * lw) / 3 ? 'right' : 'center';
      const row = cy < lh / 3 ? 'top' : cy > (2 * lh) / 3 ? 'bottom' : 'center';
      return `${row}-${col}`;
    };

    const activeAlign = getActiveAlign();
    const isImageSelected = layers.some((layer) => layer.layerId === activeLayerId && layer.layerType === 'image');
    const positionCells: Array<{ alignX: 'left' | 'center' | 'right'; alignY: 'top' | 'center' | 'bottom' }> = [
      { alignX: 'left', alignY: 'top' },
      { alignX: 'center', alignY: 'top' },
      { alignX: 'right', alignY: 'top' },
      { alignX: 'left', alignY: 'center' },
      { alignX: 'center', alignY: 'center' },
      { alignX: 'right', alignY: 'center' },
      { alignX: 'left', alignY: 'bottom' },
      { alignX: 'center', alignY: 'bottom' },
      { alignX: 'right', alignY: 'bottom' },
    ];

    return (
      <>
        <div className="editor_card layout_setting" style={{ marginBottom: '15px' }}>
          <button
            className="apply_overlay_btn"
            onClick={() => window.dispatchEvent(new CustomEvent('save-final-design'))}
            disabled={isSavingDesign}
          >
            {isSavingDesign ? 'Saving...' : 'Save Design'}
          </button>
        </div>

        <div className="editor_card">
          <h3>Overlays</h3>
          <div className="overlay_tabs">
            <figure className="tab_btn" onClick={() => setActiveOverlay?.('text')}>
              <TitleIcon sx={{ fontSize: 20 }} />
              <figcaption>Text</figcaption>
            </figure>
            <figure className="tab_btn" onClick={() => setActiveOverlay?.('color')}>
              <ColorLensIcon sx={{ fontSize: 20 }} />
              <figcaption>Color</figcaption>
            </figure>
            <figure className="tab_btn active" onClick={() => { setActiveOverlay?.('upload'); fileInputRef.current?.click(); }}>
              <img src="/static/images/upload.svg" alt="color_upload_icon" />
              <figcaption>Upload</figcaption>
            </figure>
            <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleFileInputChange} />
          </div>
        </div>

        <div className="editor_card layout_setting">
          <h3>Layout Setting</h3>
          {isImageSelected && selectedObjectProps?.src && (
            <div className="file_card">
              <img src={selectedObjectProps.src} alt="uploaded_photo" className="thumb" />
              <div className="file_info">
                <strong>{selectedObjectProps.layerName || activeLayerId}</strong>
              </div>
              <button className="del_btn" onClick={() => activeLayerId && handleDeleteLayer(activeLayerId)}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
          )}

          {!isImageSelected && (
            <div className="file_card" style={{ opacity: 0.5 }}>
              <div className="file_info">
                <strong>Select an image layer to edit layout</strong>
              </div>
            </div>
          )}

          <div className="position_control">
            <span>Position</span>
            <div className="control_body">
              <div className="position_grid">
                {positionCells.map((cell) => {
                  const key = `${cell.alignY}-${cell.alignX}`;
                  return (
                    <span
                      key={key}
                      className={`dot ${activeAlign === key ? 'active' : ''}`}
                      onClick={() => handlePositionSelect(cell.alignX, cell.alignY)}
                    />
                  );
                })}
              </div>
              <div className="coord_inputs">
                <div className="coord_row">
                  <span>X</span>
                  <div className="dropdown">
                    <input
                      type="number"
                      value={selectedObjectProps && selectedObjectProps.left !== undefined ? Math.round(selectedObjectProps.left) : ''}
                      placeholder="0"
                      onChange={(e) => handleCoordChange('left', e.target.value)}
                      disabled={!activeLayerId}
                      style={{ width: '100%', background: 'transparent', border: 'none', color: '#fff', outline: 'none' }}
                    />
                  </div>
                </div>
                <div className="coord_row">
                  <span>Y</span>
                  <div className="dropdown">
                    <input
                      type="number"
                      value={selectedObjectProps && selectedObjectProps.top !== undefined ? Math.round(selectedObjectProps.top) : ''}
                      placeholder="0"
                      onChange={(e) => handleCoordChange('top', e.target.value)}
                      disabled={!activeLayerId}
                      style={{ width: '100%', background: 'transparent', border: 'none', color: '#fff', outline: 'none' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="setting_row">
            <div className="setting_header">
              <span>Scale</span>
              <span className="value">{selectedObjectProps ? Math.round(selectedObjectProps.scale * 100) : 100}%</span>
            </div>
            <div className="slider_control">
              <Slider className="slider" value={selectedObjectProps ? selectedObjectProps.scale : 1} min={0.1} max={3} step={0.01} onChange={(_e, val) => handleSliderChange('scale', val as number)} disabled={!activeLayerId} />
            </div>
          </div>

          <div className="setting_row">
            <div className="setting_header">
              <span>Rotate</span>
              <span className="value">{selectedObjectProps ? Math.round(selectedObjectProps.angle) : 0}&deg;</span>
            </div>
            <div className="slider_control">
              <Slider className="slider" value={selectedObjectProps ? selectedObjectProps.angle : 0} min={-180} max={180} step={1} onChange={(_e, val) => handleSliderChange('angle', val as number)} disabled={!activeLayerId} />
            </div>
          </div>

          <div className="setting_row">
            <div className="setting_header">
              <span>Opacity</span>
              <span className="value">{selectedObjectProps ? Math.round(selectedObjectProps.opacity * 100) : 100}%</span>
            </div>
            <div className="slider_control">
              <Slider className="slider" value={selectedObjectProps ? selectedObjectProps.opacity : 1} min={0} max={1} step={0.01} onChange={(_e, val) => handleSliderChange('opacity', val as number)} disabled={!activeLayerId} />
            </div>
          </div>

          <div className="setting_row">
            <div className="setting_header">
              <span>Border Radius</span>
              <span className="value">{selectedObjectProps ? Math.round(selectedObjectProps.borderRadius || 0) : 0}%</span>
            </div>
            <div className="slider_control">
              <Slider
                className="slider"
                value={selectedObjectProps?.borderRadius || 0}
                min={0}
                max={100}
                step={1}
                onChange={(_e, val) => handleSliderChange('borderRadius', val as number)}
                disabled={!isImageSelected}
              />
            </div>
          </div>

          <button
            className="apply_overlay_btn"
            onClick={() => window.dispatchEvent(new CustomEvent('save-current-design', { detail: { status: 'Draft' } }))}
            disabled={isSavingDesign}
          >
            {isSavingDesign ? 'Applying...' : 'Apply Overlay'}
          </button>
        </div>

        <div className="editor_card layers_card">
          <h3>Layers</h3>
          <LayersList onDelete={handleDeleteLayer} />
        </div>
        <div style={{ height: '40px', flexShrink: 0 }} />
        <Loader isLoad={isUploadingMedia} />

        <RemoveBackgroundModal
          open={isModalOpen}
          file={pendingFile}
          isProcessing={isRemovingBg}
          isUploading={isUploadingMedia}
          processingMessage={bgProgress?.message}
          processingProgress={bgProgress?.progress}
          errorMessage={bgError}
          onRemoveBackground={handleRemoveBackground}
          onKeepBackground={handleKeepBackground}
          onCancel={handleCancelModal}
          onRetry={handleRemoveBackground}
        />
      </>
    );
};

export default UploadSettingsSidebar;
